import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency, formatPercentage, getROIColor } from '@/lib/businesscaseCalculations';
import { useNavigate } from 'react-router-dom';

interface BusinesscaseData {
  id: string;
  name: string;
  roi_year1: number;
  roi_calculation_mode?: 'bruto' | 'netto';
  net_investment: number;
  payback_period: number | null;
  projects?: { name: string; project_number?: string };
}

interface BusinesscaseComparisonProps {
  businesscases: BusinesscaseData[];
}

export function BusinesscaseComparison({ businesscases }: BusinesscaseComparisonProps) {
  const navigate = useNavigate();

  // Sort by ROI descending
  const sortedBusinesscases = [...businesscases]
    .filter(bc => !bc.name.includes('(Archief)')) // Filter out archived
    .sort((a, b) => (b.roi_year1 || 0) - (a.roi_year1 || 0));

  // Prepare chart data
  const chartData = sortedBusinesscases.map(bc => ({
    ...bc,
    displayName: bc.name.length > 25 ? bc.name.substring(0, 25) + '...' : bc.name,
  }));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>ROI Year 1 Vergelijking</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Vergelijk de Return on Investment van verschillende businesscases
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Bar Chart Visualization */}
          <div className="h-[400px] mb-8">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" unit="%" />
                <YAxis 
                  dataKey="displayName" 
                  type="category" 
                  width={180}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload as BusinesscaseData;
                      return (
                        <div className="bg-white dark:bg-gray-800 p-4 border rounded shadow-lg">
                          <p className="font-semibold">{data.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Project: {data.projects?.name || 'N/A'}
                          </p>
                          <p className="text-sm mt-2">
                            <span className="font-medium">ROI Year 1:</span>{' '}
                            <span style={{ color: getROIColor(data.roi_year1) }}>
                              {formatPercentage(data.roi_year1)}
                            </span>
                          </p>
                          <p className="text-sm">
                            <span className="font-medium">Mode:</span>{' '}
                            {data.roi_calculation_mode === 'netto' ? 'Netto' : 'Bruto'}
                          </p>
                          <p className="text-sm">
                            <span className="font-medium">Investering:</span>{' '}
                            {formatCurrency(data.net_investment)}
                          </p>
                          <p className="text-sm">
                            <span className="font-medium">Payback:</span>{' '}
                            {data.payback_period ? `${data.payback_period} jaar` : 'N/A'}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar 
                  dataKey="roi_year1" 
                  fill="hsl(var(--primary))"
                  radius={[0, 8, 8, 0]}
                  cursor="pointer"
                  onClick={(data: any) => {
                    navigate(`/crm/businesscases/${data.id}`);
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Table View */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Businesscase</TableHead>
                <TableHead>Project</TableHead>
                <TableHead className="text-right">ROI Year 1</TableHead>
                <TableHead className="text-center">Mode</TableHead>
                <TableHead className="text-right">Netto Investering</TableHead>
                <TableHead className="text-right">Payback</TableHead>
                <TableHead className="text-right">Actie</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedBusinesscases.map((bc) => (
                <TableRow 
                  key={bc.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/crm/businesscases/${bc.id}`)}
                >
                  <TableCell className="font-medium">{bc.name}</TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{bc.projects?.name || 'N/A'}</div>
                      {bc.projects?.project_number && (
                        <div className="text-xs text-muted-foreground">
                          #{bc.projects.project_number}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span 
                      className="font-semibold"
                      style={{ color: getROIColor(bc.roi_year1) }}
                    >
                      {formatPercentage(bc.roi_year1)}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={bc.roi_calculation_mode === 'netto' ? 'default' : 'secondary'}>
                      {bc.roi_calculation_mode === 'netto' ? 'Netto' : 'Bruto'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(bc.net_investment)}
                  </TableCell>
                  <TableCell className="text-right">
                    {bc.payback_period ? `${bc.payback_period} jaar` : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-xs text-primary hover:underline">
                      Bekijk →
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {sortedBusinesscases.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Geen businesscases om te vergelijken
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
